import React, { useState, SyntheticEvent, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextInput, Button, Loading } from "@alm/flow-ds/components";
import { authenticated } from "data/app/appActions";
import LoginLayout from "components/Layouts/LoginLayout";
import tokens from "lib/api/services/tokens";
import { useTranslation } from "react-i18next";
import { State } from "data";
import { getAuthType, getAuthClientId } from "data/app/appSelectors";
import AzureLogin from "./AzureLogin";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";

function Login() {
	const { t } = useTranslation();

	const dispatch = useDispatch();

	// State
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");

	// Auth provider
	const authType = useSelector((state: State) => getAuthType(state));
	const clientId = useSelector((state: State) => getAuthClientId(state));

	const attemptLogin = useCallback(
		(u: string, p: string, oauth = false) => {
			setLoading(true);
			tokens
				.create({ username: u, password: p }, oauth)
				.then(() => {
					dispatch(authenticated());
					addSentryBreadcrumb({
						category: "login",
						message: "Login successful",
					});
				})
				.catch((error: Error) => {
					if (error.message) {
						setError(t("alerts.loginError"));
						setLoading(false);
						console.log("Error", error.message);
					}

					addSentryBreadcrumb({
						category: "login",
						message: "Login failed",
					});
				});
		},
		[dispatch, t]
	);

	const localLogin = useCallback(() => {
		attemptLogin(username, password);
	}, [attemptLogin, password, username]);

	const azureLogin = useCallback(
		(token: string) => {
			attemptLogin("", token, true);
		},
		[attemptLogin]
	);

	return (
		<LoginLayout>
			{loading || authType === undefined ? (
				// Loading
				<Loading className="mt-3 mb-3 elem__loader--lg" />
			) : (
				//
				<>
					{error ? (
						<div className="alert alert-danger show" role="alert">
							<p className="mb-0">{error}</p>
						</div>
					) : null}

					{clientId && authType === "azure" ? (
						// Azure Login
						<AzureLogin
							clientId={clientId}
							setError={setError}
							attemptLogin={azureLogin}
						/>
					) : (
						// Local Login
						<form
							className="login-form"
							onSubmit={(e: SyntheticEvent<HTMLFormElement>) => {
								e.preventDefault();
								localLogin();
							}}>
							<TextInput
								label={t("main.login.username")}
								required={true}
								id="username"
								value={username}
								onChange={(
									e: SyntheticEvent<HTMLInputElement>
								) => setUsername(e.currentTarget.value)}
							/>
							<TextInput
								label={t("main.login.password")}
								required={true}
								id="password"
								value={password}
								type="password"
								onChange={(
									e: SyntheticEvent<HTMLInputElement>
								) => setPassword(e.currentTarget.value)}
							/>
							<div className="d-grid">
								<Button
									label={t("main.login.login")}
									id="loginBtn"
									className="mb-0"
								/>
							</div>
						</form>
					)}
				</>
			)}
		</LoginLayout>
	);
}

export default Login;
